import { DragIndicator, MoreVert } from '@mui/icons-material';
import { Box, ButtonGroup, IconButton } from '@mui/material';
import { UseTreeItemLabelSlotOwnProps } from '@mui/x-tree-view';
import { TreeItemLabel } from '@mui/x-tree-view/TreeItem';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../app/store';
import { getEntityIcon } from '../../entityIcon';
import { useLibraryTreeContext } from '../LibraryTreeContext';
import { ActionMenu } from './ActionMenu';

interface TCustomLabelProps extends UseTreeItemLabelSlotOwnProps {
  entityid: string;
  entitytype?: string;
  expanded?: boolean;
  editable?: boolean;
}

const CustomLabel = ({
  editable,
  children,
  entityid,
  entitytype,
  expanded,
  ...other
}: TCustomLabelProps) => {
  const dropdownMenuAnchorRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const { selectedItem, allowedActions } = useSelector(
    (state: RootState) => state.library,
  );
  // Gate the drag handle on the same permission that gates the tree's
  // itemsReordering flag in DynamicTree, so we don't dangle an affordance
  // users can't actually use.
  const isReorderingEnabled = !!allowedActions.find(({ action }) =>
    action?.includes('add-deliverable-to-directory'),
  );
  // Hide the drag handle only for a sole root-level item. We read root info
  // from LibraryTreeContext (provided by DynamicTree) so we don't subscribe
  // to useGetApiLibraryTreeQuery per tree row — one subscription instead of
  // N, which dramatically reduces re-render thrash on larger trees.
  const { rootItemIds } = useLibraryTreeContext();
  const isAtRoot = rootItemIds.has(entityid);
  const canShowDragHandle =
    isReorderingEnabled && !(isAtRoot && rootItemIds.size <= 1);

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      // Only stop propagation if this label's item is already selected
      if (selectedItem && selectedItem?.id === entityid) {
        e.stopPropagation();
      }

      setOpen((prevOpen) => !prevOpen);
    },
    [selectedItem, entityid],
  );

  return (
    <TreeItemLabel
      {...other}
      editable={editable}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        {/* Drag handle — hover-revealed via the .library-drag-handle hover
            rule in AthenaTreeItem (StyledComponents.tsx). Whole row is
            draggable via MUI X; the handle is a visual hint, not the actual
            drag target. */}
        {canShowDragHandle && (
          <Box
            className="library-drag-handle"
            aria-hidden
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled',
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' },
            }}
          >
            <DragIndicator fontSize="small" />
          </Box>
        )}
        {getEntityIcon(entitytype, !!expanded)}
        <Box
          component="span"
          // Per-entity font-weight cascade: Program 700, Project 600, others
          // 500 (default). Reinforces the hierarchy gradient at a glance.
          // To roll back, drop the fontWeight sx and let
          // AthenaTreeItem's default `fontWeight: 500` apply uniformly.
          sx={{
            minWidth: 0,
            fontWeight:
              entitytype === 'program'
                ? 700
                : entitytype === 'project'
                  ? 600
                  : 500,
          }}
        >
          {children}
        </Box>
      </Box>
      <ButtonGroup
        size="small"
        variant="text"
        sx={{ gap: 0.5, justifyContent: 'space-between' }}
      >
        <IconButton
          size="small"
          ref={dropdownMenuAnchorRef}
          onClick={handleToggle}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="menu"
          // Marker class so DynamicTree's onItemClick can skip
          // expand/collapse when the click originated here.
          className="library-kebab-trigger"
          sx={{ color: 'text.secondary' }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </ButtonGroup>
      <ActionMenu
        anchorRef={dropdownMenuAnchorRef}
        open={open}
        handleOpen={(state: boolean) => setOpen(state)}
        entityType={entitytype}
        entityId={entityid}
      />
    </TreeItemLabel>
  );
};

export default CustomLabel;
